import {
  Contracts,
  DataField,
  DataCategories,
  DiagnosticLevel,
  IActivityScope,
  SuppressNexus,
  TelemetryLogger,
  TelemetryEvent,
  makeStringDataField,
  makeInt64DataField,
} from '@microsoft/oteljs';
import { OneDSSink, OneDSEndpoint } from '@microsoft/oteljs-1ds';
import { TelemetrySink } from '@microsoft/oteljs';

import { AppContext, IAppDefinition } from './hosted-web-content-sdk.interface';
import { ITelemetryService, TelemetryScenarioName, ITelemetryScenario, TelemetryPhaseName } from './interfaces';
import { HostClientType } from './messages.interface';
import { HubSdkVersion } from './constants';
import { HubInfo } from '../meta-os-app-container.interface';
import { checkIfTestRun } from './utils';

let getCurrentMicroseconds = () => Date.now() * 1000;
if (typeof window === 'object' && typeof window.performance === 'object' && 'now' in window.performance) {
  getCurrentMicroseconds = () => Math.floor(window.performance.now()) * 1000;
}

export const adjustLastMark = (clientTimestamp: number): number => {
  // timestamp provided by the caller is a UNIX timestamp
  // but internally we're using performance markers (if supported by the browser)
  // so we need to do some adjusting
  const now = Date.now();
  const nowMicroseconds = getCurrentMicroseconds();
  return nowMicroseconds - (now - clientTimestamp) * 1000;
};

/**
 * The telemetry service for Hub SDK
 */
export class HubSdkTelemetryService implements ITelemetryService {
  private readonly letterRegex: RegExp = /[a-zA-Z]/;
  private readonly consumerTid: string = '9188040d-6c67-4c5b-b112-36a304b66dad';
  private readonly telemetrySource: string = 'AppHostSdk';
  private readonly namespace: string = 'Office.AppHostingSdk';
  private readonly eventFieldPrefix: string = 'AppHost';
  private readonly ariaTenantToken: string =
    '7ada8d16f9234e2093f080920f21dd7d-41cee27e-93c2-4a6b-a7aa-2001e852d387-7123';
  private readonly nexusTenantToken: number = SuppressNexus; // Nexus is not needed as it will be deprecated
  private readonly anonymousDataFields: DataField[] = [
    ...Contracts.Office.System.User.getFields('User', {
      isAnonymous: true,
    }),
  ];
  private readonly isTest: boolean;

  private telemetryLogger: TelemetryLogger;
  private oneDsSink: OneDSSink;
  private appDefinition?: IAppDefinition;
  private appContext?: AppContext;
  private appSdkVersion?: string;

  /**
   * Create a wrapper telemetry service on top of the custom telemetry service
   * @param hostClientType the host client type
   * @param customTelemetryService the custom telemetry service
   * @param telemetrySink the telemetry sink
   */
  constructor(
    private hubInfo: HubInfo,
    appContext: AppContext,
    private customTelemetryService?: ITelemetryService,
    private telemetrySink?: TelemetrySink
  ) {
    // Create static data fields for sink
    let dataFields: DataField[] = [
      makeStringDataField(`${this.eventFieldPrefix}.HostName`, this.hubInfo.name),
      makeStringDataField(`${this.eventFieldPrefix}.HostVersion`, this.hubInfo.version),
      makeStringDataField(`${this.eventFieldPrefix}.HostSdkVersion`, HubSdkVersion),
      makeStringDataField(`${this.eventFieldPrefix}.HostSessionId`, this.hubInfo.sessionId),
      makeStringDataField(`${this.eventFieldPrefix}.TelemetrySource`, this.telemetrySource),
      makeStringDataField(
        `${this.eventFieldPrefix}.Platform`,
        appContext.app.host.clientType === HostClientType.web ? 'Web' : 'Desktop'
      ),
    ];
    this.oneDsSink = new OneDSSink(dataFields, { endpointUrl: OneDSEndpoint.PUBLIC });

    // create logger
    this.telemetryLogger = new TelemetryLogger();
    this.telemetryLogger.setTenantToken(this.namespace, this.ariaTenantToken, this.nexusTenantToken);
    this.telemetryLogger.telemetrySinks = this.telemetrySink !== undefined ? [this.telemetrySink] : [this.oneDsSink];
    this.setAppContext(appContext);
    this.isTest = checkIfTestRun();
  }

  /**
   * Initilizing a telemetry scenario based on the named API
   * @param name name of API scenario that is being run
   * @param appId appId of the hosted application
   * @param clientTimestamp timestamp for when the message was created by the App SDK
   */
  public createScenario(
    name: TelemetryScenarioName,
    appId?: string,
    clientTimestamp: number = Date.now()
  ): ITelemetryScenario {
    appId = appId ?? this.appDefinition?.appId;

    const activity = this.isTest
      ? undefined
      : this.telemetryLogger.createActivity(this.createTelemetryEvent(name, clientTimestamp, appId));
    const customScenario = this.customTelemetryService?.createScenario(name, appId, clientTimestamp);
    const shouldLogToAria = this.shouldLogToAria(name);

    return new HubSdkTelemetryScenario(this, shouldLogToAria, clientTimestamp, customScenario, activity);
  }

  /**
   * Set the App SDK version sent as the telemetry metadata
   * @param version App SDK version used by the hosted application
   */
  public setAppSdkVersion(version: string): void {
    this.appSdkVersion = version;
  }

  /**
   * Set the app context sent as the telemetry metadata
   * @param appDefinition the app definition
   */
  public setAppContext(appContext: AppContext): void {
    let prevAppContext = this.appContext;
    this.appContext = appContext;
    if (this.telemetryLogger.persistentDataFields === undefined) {
      this.telemetryLogger.persistentDataFields = [];
    }
    // NOTE: OTel suggested that we keep appending to existing OneDSSink for perf. Last-in wins.
    // Add user metadata
    if (appContext.user?.id !== prevAppContext?.user?.id) {
      let isAnonymous = !this.appContext.user?.id;
      let userDataFields: DataField[];

      if (isAnonymous) {
        userDataFields = this.anonymousDataFields;
      } else {
        let isCommercial = this.appContext.user?.tenant?.id !== this.consumerTid;
        userDataFields = [
          ...Contracts.Office.System.User.getFields('User', {
            primaryIdentityHash: this.appContext.user?.id,
            primaryIdentitySpace: isCommercial ? 'UserObjectId' : 'MSAPuid',
            tenantId: this.appContext.user?.tenant?.id,
            tenantGroup: isCommercial ? 'Commercial' : 'Consumer',
            isAnonymous: isAnonymous,
          }),
        ];
      }

      this.telemetryLogger.persistentDataFields.push(...userDataFields);
    }

    // Add session id
    if (this.appContext.app.sessionId !== prevAppContext?.app.sessionId) {
      let sessionDataFields: DataField = makeStringDataField('Session.Id', this.appContext.app.sessionId ?? '');

      this.telemetryLogger.persistentDataFields.push(sessionDataFields);
    }

    // Add language
    if (this.appContext.app.locale !== prevAppContext?.app.locale) {
      let langDataFields: DataField = makeStringDataField('Culture.UiLanguage', this.appContext.app.locale ?? '');

      this.telemetryLogger.persistentDataFields.push(langDataFields);
    }
  }

  /**
   * Set the app definition sent as the telemetry metadata
   * @param appDefinition the app definition
   */
  public setAppDefinition(appDefinition: IAppDefinition): void {
    this.appDefinition = appDefinition;
  }

  /**
   * Return whether the session id has been set
   */
  public isSessionIdSet(): boolean {
    return !!this.appContext?.app.sessionId;
  }

  /**
   * Create a telemetry event
   * Event names must contain only strings of letters starting with a capital letter, separated by periods.
   * Additionally, event names must be within your tenant's namespace
   * @param scenarioName the scenario name
   */
  private createTelemetryEvent(
    scenarioName: TelemetryScenarioName,
    clientTimestamp: number,
    appId?: string
  ): TelemetryEvent {
    return {
      eventName: `${this.namespace}.${this.sanitizeScenarioName(scenarioName)}`,
      eventFlags: {
        dataCategories: DataCategories.ProductServiceUsage,
        diagnosticLevel: DiagnosticLevel.RequiredServiceData,
      },
      dataFields: [
        // App metadata. In telemetry App is referred as HostedApp.
        // TODO: Properties to add - HostedApp.Version
        makeStringDataField('HostedAppId', appId ?? ''),
        makeStringDataField('HostedAppEntityId', this.appContext?.page.id ?? ''),
        makeStringDataField('HostedAppSdkVersion', this.appSdkVersion ?? ''),
        makeStringDataField('HostedAppFrameContext', this.appContext?.page.frameContext ?? ''),
        makeStringDataField('HostedAppSessionId', this.appContext?.app.sessionId ?? ''),
      ],
      timestamp: clientTimestamp,
    };
  }

  /**
   * Helper function to sanitize scenario name to work with OTel/Aria
   * This function will remove non-letter characters and
   * capitalize the first character and any character after non-letter character
   * @param name the sanitized name
   */
  private sanitizeScenarioName(name: TelemetryScenarioName): string {
    let sanitizedName = '';
    let capitalize = true;

    for (let i = 0; i < name.length; i++) {
      let ch = name.charAt(i);
      if (ch === '.' && i !== 0 && sanitizedName.charAt(sanitizedName.length - 1) !== '.') {
        sanitizedName += ch;
        capitalize = true;
      } else if (this.letterRegex.test(ch)) {
        if (capitalize) {
          capitalize = false;
          sanitizedName += ch.toUpperCase();
        } else {
          sanitizedName += ch;
        }
      } else {
        capitalize = true;
      }
    }

    return sanitizedName;
  }

  private shouldLogToAria(name: TelemetryScenarioName) {
    if (name.startsWith('eventDispatch')) {
      return false;
    }

    return true;
  }
}

/**
 * HubSdkTelemetryScenario represents a telemetry scenario logged by the Hub SDK
 */
class HubSdkTelemetryScenario implements ITelemetryScenario {
  private lastMark: number;

  constructor(
    private readonly logger: HubSdkTelemetryService,
    private readonly shouldLogToAria: boolean,
    readonly clientTimestamp: number,
    private readonly customScenario?: ITelemetryScenario,
    private readonly activity?: IActivityScope
  ) {
    this.lastMark = adjustLastMark(clientTimestamp);
  }

  /**
   * Marks an intermediate point in the overall scenario timeline
   */
  public markPhase(name: TelemetryPhaseName): void {
    const now = getCurrentMicroseconds();
    const duration = now - this.lastMark;
    this.lastMark = now;

    this.activity?.dataFields?.push(makeInt64DataField(`Activity.Phase.${name}`, duration));
    this.customScenario?.markPhase(name);
  }

  /**
   * Marks the completion of this particular scenario
   */
  public stop(): void {
    this.endActivity(true);
    this.customScenario?.stop();
  }

  /**
   * Marks a failure of this scenario to complete
   * @param error error information
   */
  public fail(error?: string): void {
    this.activity?.dataFields?.push(makeStringDataField('ErrorMessage', error ?? ''));
    this.endActivity(false);
    this.customScenario?.fail(error);
  }

  /**
   * Helper function to end activity
   * @param success whether the activity completed successfully
   */
  private endActivity(success: boolean): void {
    // session id must be set first for COGS saving
    if (this.shouldLogToAria && this.activity && this.logger.isSessionIdSet()) {
      this.activity.success = success;
      this.activity.endNow();
    }
  }
}
